import { AppCheckbox, AppDialog, AppInput, AppSelect } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Label } from '@9b3kuq5phwuk4txw/kiban-react-ui/primitives'
import { TagSelect } from '@opportunities/list/-components/TagSelect'
import type { OpportunityListItem } from '@opportunities/list/-hooks/useOpportunities'
import { Save, Search } from 'lucide-react'
import { OPPORTUNITY_TYPE_IDS } from 'shared'
import type { OpportunityStatus } from '@/lib'
import { useCustomSearchDialog } from './hooks'

/**
 * CustomSearchDialog コンポーネントの Props
 *
 * @param open - ダイアログの表示状態
 * @param onOpenChange - ダイアログの表示状態変更ハンドラ
 * @param opportunities - オポチュニティ一覧（名前解決用）
 * @param opportunityStatuses - ステータス一覧（名前解決用）
 * @param onSearch - 検索実行ハンドラ
 * @param currentSearchText - 現在の検索クエリ（ダイアログを開いたときに反映する）
 */
interface CustomSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunities: OpportunityListItem[]
  opportunityStatuses: OpportunityStatus[]
  onSearch: (searchText: string) => void
  currentSearchText?: string
}

/**
 * オポチュニティ詳細検索ダイアログ
 *
 * 複数条件でのフィルタリングと検索クエリの保存機能を提供します
 */
export function CustomSearchDialog({
  open,
  onOpenChange,
  opportunities,
  opportunityStatuses,
  onSearch,
  currentSearchText,
}: CustomSearchDialogProps) {
  const {
    step,
    queryName,
    searchState,
    isSearchEnabled,
    captains,
    customers,
    verticalDivisions,
    lateralDivisions,
    salesRecordingGroups,
    setStep,
    setQueryName,
    setSearchState,
    handleOpenChange,
    handleSearch,
    handleSaveQuery,
    handleStatusToggle,
  } = useCustomSearchDialog({
    open,
    opportunities,
    onOpenChange,
    onSearch,
    currentSearchText,
  })

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={step === 1 ? '詳細検索' : '検索条件を保存'}
      className='sm:max-w-5xl max-h-[90vh] overflow-y-auto'
      footerOptions={
        step === 1
          ? [
              {
                label: '名前を付けて保存',
                onClick: () => setStep(2),
                variant: 'save' as const,
                disabled: !isSearchEnabled,
                icon: <Save />,
              },
              { label: 'キャンセル', onClick: () => onOpenChange(false), variant: 'outline' },
              {
                label: '検索',
                onClick: handleSearch,
                disabled: !isSearchEnabled,
                icon: <Search />,
              },
            ]
          : [
              { label: 'キャンセル', onClick: () => setStep(1), variant: 'outline' },
              {
                label: '保存して検索',
                onClick: handleSaveQuery,
                variant: 'save' as const,
                disabled: !queryName,
                icon: <Save />,
              },
            ]
      }
    >
      {step === 1 ? (
        <div className='space-y-4'>
          <AppInput
            id='query'
            label='含む（複数指定時は半角スペース区切り）'
            placeholder='例: プロジェクト名 キーワード'
            value={searchState.query}
            onChange={(e) => setSearchState((prev) => ({ ...prev, query: e.target.value }))}
            helpText='複数指定時は半角スペース区切り'
          />

          <AppInput
            id='exclude'
            label='含まない（複数指定時は半角スペース区切り）'
            placeholder='例: テスト サンプル'
            value={searchState.exclude}
            onChange={(e) => setSearchState((prev) => ({ ...prev, exclude: e.target.value }))}
            helpText='複数指定時は半角スペース区切り'
          />

          <TagSelect
            value={searchState.tags}
            onChange={(tags) => setSearchState((prev) => ({ ...prev, tags }))}
            label='検索するタグ（複数指定可）'
            placeholder='タグを検索...'
            collapsible
          />

          <TagSelect
            value={searchState.excludeTags}
            onChange={(tags) => setSearchState((prev) => ({ ...prev, excludeTags: tags }))}
            label='除外するタグ（複数指定可）'
            placeholder='除外するタグを検索...'
            collapsible
          />

          <div className='flex gap-4'>
            <AppSelect
              id='customer'
              label='取引先'
              value={searchState.customerId?.toString() || 'all'}
              onValueChange={(value) =>
                setSearchState((prev) => ({ ...prev, customerId: value === 'all' ? null : Number(value) }))
              }
              placeholder='取引先名で検索'
              options={[
                { value: 'all', label: 'すべて' },
                ...customers.map((customer) => ({
                  value: customer.id.toString(),
                  label: customer.name || '',
                })),
              ]}
            />
            <AppSelect
              id='verticalDivision'
              label='縦組織'
              value={searchState.verticalDivisionId?.toString() || 'all'}
              onValueChange={(value) =>
                setSearchState((prev) => ({ ...prev, verticalDivisionId: value === 'all' ? null : Number(value) }))
              }
              placeholder='縦組織で検索'
              options={[
                { value: 'all', label: 'すべて' },
                ...verticalDivisions.map((division) => ({
                  value: division.id.toString(),
                  label: division.shortName,
                })),
              ]}
            />
            <AppSelect
              id='lateralDivision'
              label='横組織'
              value={searchState.lateralDivisionId?.toString() || 'all'}
              onValueChange={(value) =>
                setSearchState((prev) => ({ ...prev, lateralDivisionId: value === 'all' ? null : Number(value) }))
              }
              placeholder='横組織で検索'
              options={[
                { value: 'all', label: 'すべて' },
                ...lateralDivisions.map((division) => ({
                  value: division.id.toString(),
                  label: division.shortName,
                })),
              ]}
            />
          </div>

          <div className='flex gap-4'>
            <AppSelect
              id='createdBy'
              label='起票者'
              value={searchState.createdBy?.toString() || 'all'}
              onValueChange={(value) =>
                setSearchState((prev) => ({ ...prev, createdBy: value === 'all' ? null : Number(value) }))
              }
              placeholder='起票者で検索'
              options={[
                { value: 'all', label: 'すべて' },
                ...captains.map((captain) => ({
                  value: captain.id.toString(),
                  label: captain.text,
                })),
              ]}
            />

            <AppSelect
              id='salesRecordingGroup'
              label='売上計上グループ'
              value={searchState.salesRecordingGroupId?.toString() || 'all'}
              onValueChange={(value) =>
                setSearchState((prev) => ({
                  ...prev,
                  salesRecordingGroupId: value === 'all' ? null : Number(value),
                }))
              }
              placeholder='売上計上グループで検索'
              options={[
                { value: 'all', label: 'すべて' },
                ...salesRecordingGroups.map((group) => ({
                  value: group.id.toString(),
                  label: group.name,
                })),
              ]}
            />
          </div>

          <div className='space-y-2'>
            <Label className='text-xs text-muted-foreground'>オポ種別</Label>
            <div className='flex gap-4'>
              <AppCheckbox
                label='すべて'
                checked={searchState.opportunityTypeId === null}
                onCheckedChange={(checked) => {
                  if (checked) setSearchState((prev) => ({ ...prev, opportunityTypeId: null }))
                }}
                labelClassName='font-normal'
              />
              <AppCheckbox
                label='キャプテンオポ'
                checked={searchState.opportunityTypeId === OPPORTUNITY_TYPE_IDS.CAPTAIN}
                onCheckedChange={(checked) => {
                  setSearchState((prev) => ({
                    ...prev,
                    opportunityTypeId: checked ? OPPORTUNITY_TYPE_IDS.CAPTAIN : null,
                  }))
                }}
                labelClassName='font-normal'
              />
              <AppCheckbox
                label='営業オポ'
                checked={searchState.opportunityTypeId === OPPORTUNITY_TYPE_IDS.SALES}
                onCheckedChange={(checked) => {
                  setSearchState((prev) => ({
                    ...prev,
                    opportunityTypeId: checked ? OPPORTUNITY_TYPE_IDS.SALES : null,
                  }))
                }}
                labelClassName='font-normal'
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Label className='text-xs text-muted-foreground'>ステータス</Label>
            <div className='flex flex-wrap gap-4'>
              {opportunityStatuses.map((status) => (
                <AppCheckbox
                  key={status.id}
                  label={status.name}
                  checked={searchState.opportunityStatusIds.includes(status.id)}
                  onCheckedChange={(checked) => handleStatusToggle(status.id, checked as boolean)}
                  labelClassName='font-normal'
                />
              ))}
            </div>
          </div>

          <div className='space-y-2'>
            <Label className='text-xs text-muted-foreground'>インセンティブ</Label>
            <div className='flex gap-4'>
              <AppCheckbox
                label='すべて'
                checked={searchState.incentiveFilter === null}
                onCheckedChange={(checked) => {
                  if (checked) setSearchState((prev) => ({ ...prev, incentiveFilter: null }))
                }}
                labelClassName='font-normal'
              />
              <AppCheckbox
                label='対象のみ'
                checked={searchState.incentiveFilter === 'target'}
                onCheckedChange={(checked) => {
                  if (checked) setSearchState((prev) => ({ ...prev, incentiveFilter: 'target' }))
                }}
                labelClassName='font-normal'
              />
              <AppCheckbox
                label='対象外'
                checked={searchState.incentiveFilter === 'disabled'}
                onCheckedChange={(checked) => {
                  if (checked) setSearchState((prev) => ({ ...prev, incentiveFilter: 'disabled' }))
                }}
                labelClassName='font-normal'
              />
            </div>
          </div>
        </div>
      ) : (
        <div className='space-y-4'>
          <AppInput
            id='queryName'
            label='検索条件名'
            placeholder='指定した検索条件にわかりやすい名前をつけてください'
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
          />
        </div>
      )}
    </AppDialog>
  )
}
